import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Check, Sparkles, Crown, ArrowUpRight } from "lucide-react";

const PLANS = [
  { id: "pro", name: "Pro", price: "$5", icon: Sparkles, color: "#FF8A00", tagline: "For focused decision-makers", features: ["Compare up to 3 decisions side-by-side", "Ranked best-fit recommendation", "Everything in Free"] },
  { id: "unlimited", name: "Unlimited", price: "$20", icon: Crown, color: "#FF4D4D", tagline: "For serious life & career planning", features: ["Compare unlimited decisions at once", "Ranked best-fit recommendation", "Priority to new premium tools", "Everything in Pro"] },
];

export default function Premium() {
  const { subscription, refreshSubscription } = useAuth();
  const [params, setParams] = useSearchParams();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshSubscription();
          toast.success("Subscription active — premium unlocked!");
          setVerifying(false);
          params.delete("session_id");
          setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          setVerifying(false);
          return;
        }
      } catch (e) { /* keep polling */ }
      attempts += 1;
      if (attempts >= 6) { setVerifying(false); toast.message("Still processing — refresh in a moment."); return; }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const subscribe = async (planId) => {
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoadingPlan(null);
    }
  };

  const manageBilling = async () => {
    try {
      const { data } = await api.post("/payments/portal", { package_id: "pro", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Billing portal is unavailable.");
    }
  };

  const active = subscription?.active;
  const recurring = subscription?.recurring;

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-14 relative z-10">
        <div className="text-center max-w-2xl mx-auto">
          <span className="label-tiny">Scorecaster Premium</span>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-3 text-ink">
            Compare your next moves,{" "}
            <span style={{ color: "#FF8A00" }}>side by side.</span>
          </h1>
          <p className="text-stone mt-5 text-base md:text-lg">
            See how your current profile stacks up across multiple decisions at once — and know exactly which move fits best right now.
          </p>
        </div>

        {verifying && <div className="mt-8 text-center text-sm text-brand-orange font-mono" data-testid="premium-verifying">Verifying your payment…</div>}

        {active && (
          <div className="mt-8 rounded-3xl border border-moss/40 bg-moss/5 p-6 text-center" data-testid="premium-active">
            <p className="font-display font-bold text-lg text-ink">You're on the <span className="text-moss">{subscription.label}</span> plan.</p>
            <p className="text-sm text-stone mt-1">{recurring ? "Auto-renews" : "Access expires"} {new Date(subscription.expires_at).toLocaleDateString()}.</p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button onClick={() => navigate("/compare")} data-testid="premium-go-compare" className="inline-flex items-center gap-2 mt-4 px-6 py-3 rounded-full bg-ink text-white font-semibold hover:bg-[#413b36] transition-colors duration-300">
                Compare decisions <ArrowUpRight size={16} />
              </button>
              {subscription.manageable && (
                <button onClick={manageBilling} data-testid="premium-manage" className="inline-flex items-center gap-2 mt-4 px-6 py-3 rounded-full border border-clay text-ink hover:border-[#DDA15E] transition-colors duration-300">
                  Manage subscription
                </button>
              )}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mt-12">
          {PLANS.map((p) => {
            const isCurrent = active && subscription.tier === p.id;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`} className="relative card-soft p-8 flex flex-col hover:-translate-y-1 transition-transform duration-500">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: `${p.color}18` }}>
                    <p.icon size={20} style={{ color: p.color }} />
                  </div>
                  <h2 className="font-display font-bold text-xl text-ink">{p.name}</h2>
                </div>
                <p className="text-sm text-stone mt-2">{p.tagline}</p>
                <div className="flex items-baseline gap-1 mt-6">
                  <span className="font-mono text-5xl font-extrabold" style={{ color: p.color }}>{p.price}</span>
                  <span className="text-stone text-sm">/month</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-ink">
                      <Check size={16} style={{ color: p.color }} className="mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => subscribe(p.id)} disabled={loadingPlan === p.id || isCurrent} data-testid={`subscribe-${p.id}`}
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold transition-colors duration-300 disabled:opacity-60"
                  style={{ background: isCurrent ? "#EADDCE" : p.color, color: isCurrent ? "#6B655F" : "#ffffff" }}>
                  {isCurrent ? "Current plan" : loadingPlan === p.id ? "Starting checkout…" : `Get ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-stone mt-8 font-mono">
          {recurring ? "Auto-renews monthly · cancel anytime · Stripe test mode (card 4242 4242 4242 4242)" : "Each payment unlocks 30 days of access · Stripe test mode (card 4242 4242 4242 4242)"}
        </p>
      </div>
    </div>
  );
}
